import React, { createRef, useRef, useState } from "react";
import { ButtonGroup, Col, Modal, Row, InputGroup, FormControl } from 'react-bootstrap';
import ReactCropper from "react-cropper";
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Button from '../Button';
import './Cropper.scss';
import "cropperjs/dist/cropper.css";
import { useTranslation } from 'react-i18next';
import { ACCEPTED_IMAGE_INPUT_ACCEPT, isAcceptedImageFile } from '../../../utils/imageUploads';

/**
 * @typedef CropperProps
 * @property {Function} onChange
 * @property {'circular'|'rectangular'|'square'} shape
 *
 * @param {CropperProps} props
 * @returns
 */
const Cropper = ({
  onChange,
  shape = "circular",
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [invalidFileType, setInvalidFileType] = useState(false);
  const { t } = useTranslation();

  const cropperRef = createRef();
  const inputRef = useRef();

  const clearSelection = ({ clearInput = false } = {}) => {
    setFile(null);
    setImageSrc(null);
    if (clearInput && inputRef.current) inputRef.current.value = null;
    if (onChange) onChange(null);
  };

  const handleOnChange = (e) => {
    e.preventDefault();
    const { files } = e.target;

    setInvalidFileType(false);

    if (!files || !files.length) {
      clearSelection();
      return;
    }
    const file = files[0];

    if (!isAcceptedImageFile(file)) {
      setInvalidFileType(true);
      clearSelection({ clearInput: true });
      return;
    }

    setFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowModal(true);
    };
    reader.readAsDataURL(files[0]);
  };

  const handleAccept = () => {
    cropperRef.current.cropper.getCroppedCanvas()
      .toBlob((blob) => {
        const newFile = new File([blob], file.name, { type: file.type });
        if (showModal && onChange) {
          onChange(newFile);
        }
        setShowModal(false);
      });
  };

  const handleClose = () => {
    setShowModal(false);
    setImageSrc(null);
    setFile(null);
    setInvalidFileType(false);
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleZoomIn = () => {
    cropperRef.current.cropper.zoom(0.1);
  };

  const handleZoomOut = () => {
    cropperRef.current.cropper.zoom(-0.1);
  };

  const handleSearchFile = () => {
    inputRef.current.click();
  };

  return (
    <React.Fragment>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <Button variant="secondary" onClick={handleSearchFile}>
            {t('common.search_file')}...
          </Button>
        </InputGroup.Prepend>
        <FormControl readOnly value={get(file, 'name', '')} />
        <input
          type="file"
          hidden
          {...props}
          onChange={handleOnChange}
          ref={inputRef}
          accept={ACCEPTED_IMAGE_INPUT_ACCEPT} />
      </InputGroup>

      {invalidFileType && <small className="tc-red text-3 d-block mb-2">
        {t('common.file_type_not_accepted')}
      </small>}

      <Modal show={showModal}
        fullscreen='md-down'
        onHide={handleClose}
        backdrop="static"
        size='lg'
        style={{ maxHeight: '90vh' }}>
        <Modal.Body >
          <ReactCropper
            className={shape === "circular" ? 'cropper-circled' : ''}
            ref={cropperRef}
            zoomTo={0}
            src={imageSrc}
            viewMode={2}
            minCropBoxHeight={100}
            minCropBoxWidth={100}
            cropBoxResizable={false}
            background={false}
            autoCropArea={1}
            checkOrientation
            rotatable
            aspectRatio={shape === "rectangular" ? 2.5 : 1}
            dragMode='move'
            movable
            responsive
            guides
            scalable
            width={400}
            height={400}
          />
          <Row className="justify-content-end">
            <Col xs="auto" className='d-flex'>
              <Button onClick={handleZoomIn}>
                <i className="fal fa-search-plus" />
              </Button>
              <Button onClick={handleZoomOut}>
                <i className="fal fa-search-minus" />
              </Button>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose} className="mr-2">
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleAccept}>
              {t('common.accept')}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

Cropper.propTypes = {
  onChange: PropTypes.func,
  shape: PropTypes.oneOf(["circular", "rectangular", "square"]),
};

Cropper.defaultProps = {
  shape: "circular",
};

export default Cropper;